import { getItemDetails, getItemsList } from 'services/dumpert';
import { cacheKey, makeLoader } from './common/loader';
import {
  getCommentsData,
  getPersonalStats,
  getProfileComments,
  getProfileBadges,
} from 'services/comments';
import { getProfilePlayLists } from 'services/post';
import { getPreferenceCookie, selectedIdParam } from './context';
import { removeEmpty } from './common/o';
import { encodeProfileUrl } from '../lib/helpers';

export const getItemsListDefaultParams = {
  list: 'latest',
  page: 0,
};

const loadAll = (loaders) =>
  Promise.allSettled(loaders.map(({ p }) => p)).then((all) =>
    all
      .map((r, i) => ({
        key: loaders[i].k,
        data:
          r.status === 'fulfilled'
            ? {
                result: { data: r.value.data, status: r.value.status },
              }
            : { error: r.reason },
      }))
      .reduce(
        (acc, { key, data }) => ({
          ...acc,
          [key]: { data, timestamp: Date.now() + 5000 },
        }),
        {},
      ),
  );

const itemDetailsLoader = (itemId, requestId, metrics, token, listParams) => {
  const detailsParams = { itemId };
  const commentsParams = {
    itemId: detailsParams.itemId.replace('_', '/'),
    user: null,
  };
  const relatedParams = { itemId, list: 'related', ...listParams };
  return [
    {
      p: makeLoader(requestId, metrics, getItemDetails, token)(detailsParams).promise,
      k: cacheKey(getItemDetails, detailsParams),
    },
    {
      p: makeLoader(requestId, metrics, getCommentsData, token)(commentsParams).promise,
      k: cacheKey(getCommentsData, commentsParams),
    },
    itemListLoader(relatedParams, requestId, metrics, token),
  ];
};

const itemListLoader = (listParams, requestId, metrics, token) => {
  const params = removeEmpty({
    ...getItemsListDefaultParams,
    ...listParams,
  });
  return {
    p: makeLoader(requestId, metrics, getItemsList, token)(params).promise,
    k: cacheKey(getItemsList, params),
  };
};

export const itemDetailsPageLoader = () => (req, match, token, metrics) => {
  const { itemId } = match.params;
  const selectedId = req.query[selectedIdParam];
  const allListsParams = {};
  const preferences = getPreferenceCookie(req.headers.cookie);
  if (preferences.nsfwActive) {
    allListsParams.nsfwActive = preferences.nsfwActive;
  }
  return loadAll(
    [].concat(
      itemDetailsLoader(itemId, req.id, metrics, token, { ...allListsParams }),
      [{ itemId }].map((p) => itemListLoader({ ...allListsParams }, req.id, metrics, token)),
      selectedId && selectedId !== itemId
        ? itemDetailsLoader(selectedId, req.id, metrics, token, {
            ...allListsParams,
          })
        : [],
    ),
  );
};
export const itemListPageLoader = (lists, type) => (req, match, token, metrics) => {
  const selectedId = req.query[selectedIdParam];
  const { period, tag, date, offset } = match.params;
  let mainListParams = {};
  const allListsParams = {};
  const activeTag = type === 'dumperttv' ? 'dumperttv' : tag;
  switch (type) {
    case 'toppers':
      if (period) {
        mainListParams = { period };
      }
      break;
    case 'search':
    case 'tag':
    case 'dumperttv':
      mainListParams = { tag: activeTag };
      break;
    case 'datum':
      mainListParams = { date };
      break;
    case 'nieuw':
      mainListParams = { period, page: offset || 0 };
      break;
    default:
      break;
  }
  const preferences = getPreferenceCookie(req.headers.cookie);
  if (preferences.filter) {
    mainListParams.filter = preferences.filter;
  }

  if (preferences.nsfwActive) {
    allListsParams.nsfwActive = preferences.nsfwActive;
  }

  return loadAll(
    lists(preferences)
      .map((params, i) =>
        itemListLoader(
          i === 0
            ? { ...params, ...allListsParams, ...mainListParams }
            : { ...params, ...allListsParams },
          req.id,
          metrics,
          token,
        ),
      )
      .concat(
        selectedId
          ? itemDetailsLoader(selectedId, req.id, metrics, token, {
              ...allListsParams,
            })
          : [],
      ),
  );
};

export const profileLoader = () => (req, match, token, metrics) => {
  const { username } = match.params;
  const encodedUsername = encodeProfileUrl(username);

  return loadAll([
    {
      p: makeLoader(req.id, metrics, getPersonalStats, token)({ username: encodedUsername })
        .promise,
      k: cacheKey(getPersonalStats, { username: encodedUsername }),
    },
    {
      p: makeLoader(req.id, metrics, getProfileComments, token)({ username: encodedUsername })
        .promise,
      k: cacheKey(getProfileComments, { username: encodedUsername }),
    },
    {
      p: makeLoader(req.id, metrics, getProfilePlayLists, token)({ username: encodedUsername })
        .promise,
      k: cacheKey(getProfilePlayLists, { username: encodedUsername }),
    },
    {
      p: makeLoader(req.id, metrics, getProfileBadges, token)({ username: encodedUsername })
        .promise,
      k: cacheKey(getProfileBadges, { username: encodedUsername }),
    },
  ]);
};
